import { useState } from 'react';
import type { cardDelivery } from '@bb/contracts';
import { Checkbox, Field, Input, RadioGroup, Select, Textarea } from '@bb/ui';
import { DurationField } from '../../../../components/DurationField.tsx';
import { AudienceBuilder, emptyAudience } from '../../../audience/AudienceBuilder.tsx';
import { QuietHoursFields } from '../../../campaigns/schedule/DeliveryControlsForm.tsx';
import { NumberInput } from '../../../campaigns/trigger/NumberInput.tsx';
import { CardCreationModeField } from '../../../composer/card/CardCreationModeField.tsx';
import { CardRemovalField, withRemovalTriggers } from '../../../composer/card/CardRemovalField.tsx';
import { PushComposer } from '../../../composer/push/PushComposer.tsx';
import { detectMode, emptyContent, PLATFORMS, writeShared, type PushComposerMode } from '../../../composer/push/pushDraft.ts';
import { MessageTestPanel } from '../../../composer/shared/MessageTestPanel.tsx';
import { WebhookComposer } from '../../../composer/webhook/WebhookComposer.tsx';
import { TemplateActions } from '../../../templates/TemplatePickerDialog.tsx';
import { insertTemplate, templateChannelOf, templateContentOf } from '../../../templates/templateContent.ts';
import type { TestSendChannel } from '../../../../api/client.ts';
import {
  CARD_EXPIRY_MAX_SECONDS,
  CARD_TYPES,
  cardFields,
  cardIssues,
  messageIssues,
  pushValue,
  readMessage,
  setCardField,
  setChannel,
  setContent,
  setPush,
  setQuietHours,
  type ChannelContent,
  type DeliveryChannel,
  type MessageConfig,
  type Policy,
} from './messageConfig.ts';

export interface MessageStepPanelProps {
  environmentId: string;
  /** The step's `config` as stored. */
  value: unknown;
  /** Every edit, with the server's defaults filled in. */
  onChange: (next: MessageConfig) => void;
  readOnly?: boolean;
  /** Shown on the composers when read-only. */
  readOnlyNotice?: string;
  /** The step is in the running version: the card creation mode is fixed. */
  launched?: boolean;
}

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

const POLICY_OPTIONS: Array<{ value: Policy; label: string }> = [
  { value: 'advance', label: 'Go on to the next step' },
  { value: 'exit', label: 'End the Canvas for this user' },
];

/** advance / exit for one outcome, with what it means and what happens now. */
function PolicyField({ label, explanation, who, exitReason, value, readOnly, onChange }: { label: string; explanation: string; who: string; exitReason: string; value: Policy; readOnly: boolean; onChange: (next: Policy) => void }) {
  return (
    <div className="step-panel__policy">
      <p className="bb-field__label">{label}</p>
      <p className="hint">{explanation}</p>
      <RadioGroup label={label} options={POLICY_OPTIONS.map((o) => ({ ...o, disabled: readOnly }))} value={value} onValueChange={(v) => onChange(v as Policy)} />
      <p className="hint">{value === 'advance' ? `Now: ${who} go on to the next step.` : `Now: ${who} leave the Canvas (exit reason ${exitReason}).`}</p>
    </div>
  );
}

type AudienceCheck = 'none' | 'canvas' | 'custom';
const checkOf = (c: MessageConfig): AudienceCheck => (c.validateAudienceAtSend === undefined ? 'none' : c.validateAudienceAtSend === 'canvas_audience' ? 'canvas' : 'custom');

function DeliverySettings({ environmentId, c, readOnly, onChange }: { environmentId: string; c: MessageConfig; readOnly: boolean; onChange: (next: MessageConfig) => void }) {
  const check = checkOf(c);
  const setCheck = (next: AudienceCheck) => {
    if (next === check) return;
    const { validateAudienceAtSend: _old, ...rest } = c;
    onChange(next === 'none' ? rest : { ...rest, validateAudienceAtSend: next === 'canvas' ? 'canvas_audience' : emptyAudience() });
  };
  return (
    <fieldset className="stack-form">
      <legend>Delivery settings</legend>
      <p className="hint">Sent, capped by a frequency rule or already sent for this entry: the user always goes on to the next step. Held back by quiet hours: the user waits in this step and the message goes out when they end.</p>
      <PolicyField
        label="If the message is suppressed"
        explanation="Suppressed: the user cannot get the message — no consent or subscription, in a suppression list or the global control group, or no device that can receive it. Default: go on."
        who="users whose message is suppressed"
        exitReason="message_suppressed"
        value={c.onSuppressed}
        readOnly={readOnly}
        onChange={(onSuppressed) => onChange({ ...c, onSuppressed })}
      />
      <PolicyField
        label="If sending fails"
        explanation="Failed: the message could not be rendered or the provider refused it. Default: go on."
        who="users whose message fails"
        exitReason="message_failed"
        value={c.onFailed}
        readOnly={readOnly}
        onChange={(onFailed) => onChange({ ...c, onFailed })}
      />
      <section aria-label="Validate audience at send" className="step-panel__validate">
        <h4>Validate audience at send</h4>
        <p className="hint">Checked right before the message goes out, on the user’s current data.</p>
        <RadioGroup
          label="Audience checked at send"
          options={[
            { value: 'none', label: 'Don’t check', disabled: readOnly },
            { value: 'canvas', label: 'The Canvas entry audience', disabled: readOnly },
            { value: 'custom', label: 'A custom audience', disabled: readOnly },
          ]}
          value={check}
          onValueChange={(v) => setCheck(v as AudienceCheck)}
        />
        {isObject(c.validateAudienceAtSend) && <AudienceBuilder environmentId={environmentId} value={c.validateAudienceAtSend as never} readOnly={readOnly} onChange={(audience) => onChange({ ...c, validateAudienceAtSend: audience })} />}
        {check !== 'none' && (
          <PolicyField
            label="If the user no longer matches"
            explanation="The user left the audience while waiting in the Canvas. Default: end the Canvas, as Braze does."
            who="users who no longer match"
            exitReason="message_validation_failed"
            value={c.onValidationFailure}
            readOnly={readOnly}
            onChange={(onValidationFailure) => onChange({ ...c, onValidationFailure })}
          />
        )}
      </section>
    </fieldset>
  );
}

/** `2026-11-01T10:00:00.000Z` ↔ the `datetime-local` value in UTC. */
const toLocalInput = (iso: unknown) => (typeof iso === 'string' && !Number.isNaN(Date.parse(iso)) ? new Date(iso).toISOString().slice(0, 16) : '');
const fromLocalInput = (value: string) => (value ? new Date(`${value}:00Z`).toISOString() : '');

/**
 * The card of the step (also a campaign variant's card, BB-97): type and texts (the full card composer is BB-62),
 * creation mode (BB-63), expiry (BB-64) and removal events (BB-65).
 */
export function CardContentFields({
  environmentId,
  content,
  readOnly,
  launched,
  onChange,
  durationLabel = 'A set time after the user reaches this step',
}: {
  environmentId: string;
  content: ChannelContent;
  readOnly: boolean;
  launched: boolean;
  onChange: (next: ChannelContent) => void;
  /** What a duration expiry counts from. */
  durationLabel?: string;
}) {
  const f = cardFields(content);
  const issues = cardIssues(content);
  const expiry = isObject(content.expiry) ? content.expiry : { mode: 'duration', seconds: CARD_EXPIRY_MAX_SECONDS };
  const needsImage = f.type !== 'classic';
  return (
    <section aria-label="Content Card" className="step-panel__channel">
      <h4>Content Card</h4>
      <Field label="Card type">
        <Select options={CARD_TYPES.map((t) => ({ value: t.value, label: t.label }))} value={f.type} disabled={readOnly} onChange={(e) => onChange(setCardField(content, 'type', e.target.value))} />
      </Field>
      <Field label="Card title" error={issues.title}>
        <Input value={f.title} maxLength={500} disabled={readOnly} onChange={(e) => onChange(setCardField(content, 'title', e.target.value))} />
      </Field>
      <Field label="Card description" error={issues.description}>
        <Textarea value={f.description} maxLength={2000} rows={3} disabled={readOnly} onChange={(e) => onChange(setCardField(content, 'description', e.target.value))} />
      </Field>
      {(needsImage || f.imageUrl) && (
        <div className="audience-filter">
          <Field label="Image URL" error={issues.imageUrl}>
            <Input value={f.imageUrl} type="url" spellCheck={false} disabled={readOnly} onChange={(e) => onChange(setCardField(content, 'imageUrl', e.target.value.trim()))} />
          </Field>
          <Field label="Image aspect ratio" hint="Width ÷ height, e.g. 1.33 for 4:3." error={issues.aspectRatio}>
            <NumberInput min={0} max={10} step="any" value={f.aspectRatio} disabled={readOnly} onValueChange={(n) => onChange(setCardField(content, 'aspectRatio', n))} />
          </Field>
        </div>
      )}
      <p className="hint">The click action, pinning, dismissal and key-value pairs come with the card composer (BB-62); what is stored for them is kept.</p>
      <CardCreationModeField value={(content.creationMode as cardDelivery.CardCreationMode | undefined) ?? 'first_impression'} locked={launched} disabled={readOnly} onChange={(creationMode) => onChange({ ...content, creationMode })} />
      <div className="step-panel__choice">
        <p className="bb-field__label">Card expires</p>
        <RadioGroup
          label="Card expires"
          options={[
            { value: 'duration', label: durationLabel, disabled: readOnly },
            { value: 'at', label: 'At a date and time', disabled: readOnly },
          ]}
          value={expiry.mode === 'at' ? 'at' : 'duration'}
          onValueChange={(mode) => onChange({ ...content, expiry: mode === 'at' ? { mode: 'at', at: '' } : { mode: 'duration', seconds: CARD_EXPIRY_MAX_SECONDS } })}
        />
      </div>
      {expiry.mode === 'at' ? (
        <Field label="Expires at (UTC)" error={issues.expiry}>
          <Input type="datetime-local" value={toLocalInput(expiry.at)} disabled={readOnly} onChange={(e) => onChange({ ...content, expiry: { mode: 'at', at: fromLocalInput(e.target.value) } })} />
        </Field>
      ) : (
        <DurationField
          label="Expires after"
          seconds={typeof expiry.seconds === 'number' ? expiry.seconds : CARD_EXPIRY_MAX_SECONDS}
          units={['minutes', 'hours', 'days']}
          error={issues.expiry}
          hint="Up to 30 days."
          disabled={readOnly}
          onChange={(seconds) => onChange({ ...content, expiry: { mode: 'duration', seconds } })}
        />
      )}
      <CardRemovalField environmentId={environmentId} value={content.removalTriggers} disabled={readOnly} onChange={(triggers) => onChange(withRemovalTriggers(content, triggers))} />
    </section>
  );
}

const TEST_CHANNELS: readonly TestSendChannel[] = ['push_android', 'push_ios', 'content_card', 'webhook'];

const CHANNEL_BOXES: Array<{ channel: DeliveryChannel | 'push'; label: string }> = [
  { channel: 'push', label: 'Push' },
  { channel: 'content_card', label: 'Content Card' },
  { channel: 'in_app', label: 'In-app message' },
  { channel: 'webhook', label: 'Webhook' },
];

/**
 * Settings of a Message step (BB-115): its channels — push (Android and iOS, one shared content or one per platform,
 * through the campaign push composer), a Content Card, a webhook (BB-142) and in-app once its composer exists —, what happens
 * when the message is suppressed, fails or the user no longer matches the audience checked at send, and quiet hours
 * for this step.
 */
export function MessageStepPanel({ environmentId, value, onChange, readOnly = false, readOnlyNotice, launched = false }: MessageStepPanelProps) {
  const c = readMessage(value);
  const issues = messageIssues(c);
  const push = pushValue(c);
  const hasPush = Object.keys(push).length > 0;
  const [mode, setMode] = useState<PushComposerMode>(() => (hasPush ? detectMode(push) : 'shared'));
  const on = (channel: DeliveryChannel | 'push') => (channel === 'push' ? hasPush : c.channels.includes(channel));
  const onlyOne = CHANNEL_BOXES.filter((b) => on(b.channel)).length === 1;
  const [templateNotice, setTemplateNotice] = useState<string | null>(null);
  // Template library (BB-56): a copy of the template into the step's channels, or the channel content saved as a template.
  const templates = (channel: DeliveryChannel) => {
    const templateChannel = templateChannelOf(channel)!;
    const source = c.sourceTemplates?.[channel];
    return (
      <>
        <TemplateActions
          environmentId={environmentId}
          channel={templateChannel}
          content={templateContentOf(templateChannel, c.content)}
          readOnly={readOnly}
          onInsert={(instance) => {
            const next = insertTemplate(c, instance);
            if (next) {
              if (templateChannel === 'push') setMode(detectMode(pushValue(next)));
              onChange(next);
            }
            setTemplateNotice(next ? `Inserted «${instance.name}» v${instance.source.version}.` : `«${instance.name}» has no content for this step's platforms.`);
          }}
        />
        {source && <p className="hint">Created from a template (v{source.version}); editing it here does not change the template.</p>}
      </>
    );
  };
  const toggle = (channel: DeliveryChannel | 'push', checked: boolean) => {
    if (channel !== 'push') return onChange(setChannel(c, channel, checked));
    if (checked) setMode('shared');
    onChange(setPush(c, checked ? writeShared(emptyContent(PLATFORMS), PLATFORMS) : null));
  };
  return (
    <section className="step-panel" aria-label="Message settings">
      <fieldset className="step-panel__channels">
        <legend>Channels</legend>
        {CHANNEL_BOXES.map((b) => (
          <Checkbox key={b.channel} label={b.label} checked={on(b.channel)} disabled={readOnly || (onlyOne && on(b.channel))} onCheckedChange={(checked) => toggle(b.channel, checked)} />
        ))}
        {issues.channels && (
          <p className="bb-field__error" role="alert">
            {issues.channels}
          </p>
        )}
        {onlyOne && !readOnly && <p className="hint">A Message step needs at least one channel: add another before removing this one.</p>}
      </fieldset>
      {hasPush && (
        <PushComposer
          environmentId={environmentId}
          category="marketing"
          mode={mode}
          onModeChange={setMode}
          value={push}
          onChange={(next) => onChange(setPush(c, next))}
          readOnly={readOnly}
          {...(readOnlyNotice ? { readOnlyNotice } : {})}
        />
      )}
      {hasPush && templates(c.channels.includes('push_android') ? 'push_android' : 'push_ios')}
      {c.channels.includes('content_card') && (
        <>
          <CardContentFields environmentId={environmentId} content={c.content.content_card ?? {}} readOnly={readOnly} launched={launched} onChange={(next) => onChange(setContent(c, 'content_card', next))} />
          {templates('content_card')}
        </>
      )}
      {c.channels.includes('in_app') && (
        <section aria-label="In-app message" className="step-panel__channel">
          <h4>In-app message</h4>
          <p className="notice notice--warning">The in-app message composer for Canvas comes with BB-90. Until then the in-app content comes from a template.</p>
          {templates('in_app')}
        </section>
      )}
      {c.channels.includes('webhook') && (
        <>
          <WebhookComposer environmentId={environmentId} value={c.content.webhook ?? {}} samples={['event_properties', 'context']} readOnly={readOnly} onChange={(next) => onChange(setContent(c, 'webhook', next))} />
          {templates('webhook')}
        </>
      )}
      {templateNotice && <p role="status">{templateNotice}</p>}
      <MessageTestPanel environmentId={environmentId} channels={c.channels.filter((ch): ch is TestSendChannel => (TEST_CHANNELS as readonly string[]).includes(ch))} contentOf={(ch) => c.content[ch]} samples={['event_properties', 'context']} />
      <DeliverySettings environmentId={environmentId} c={c} readOnly={readOnly} onChange={onChange} />
      <section aria-label="Quiet hours of this step" className="step-panel__quiet">
        <p className="hint">Quiet hours for this step only. Without them this step sends at any hour. A message they hold back waits in this step until they end, or is not sent.</p>
        <QuietHoursFields quiet={c.overrides?.quietHours} issues={issues} readOnly={readOnly} onChange={(quietHours) => onChange(setQuietHours(c, quietHours))} />
      </section>
    </section>
  );
}
